<template>
  <form-layout
    class="procurement-form"
    :loading="isLoading"
    @save="saveProcurement"
  >
    <template #title>
      <h1 class="uppercase">{{ titleText }}</h1>
    </template>
    <template #body>
      <bdg-dropdown
        ref="type"
        name="Status"
        :options="statuses"
        v-model="formData.type"
        required
      />
      <bdg-multi-select
        ref="items"
        name="Items"
        begin-typing-message="Begin typing item name here"
        empty-state-message="No items added"
        :search-function="itemsSearch"
        v-model="formData.items"
        can-add
        isQty
        required
      />
      <bdg-dropdown
        ref="vendor"
        name="Vendor"
        :options="vendors"
        v-model="formData.contact_id"
        searchable
        required
      />
      <div class="order-delivery flex mt-6">
        <bdg-date-field
          ref="orderedAt"
          class="order-date"
          name="Order date"
          placeholder="mm/dd/yyyy"
          v-model="formData.ordered_at"
          required
        />
        <bdg-date-field
          ref="scheduledFor"
          class="delivery-date"
          name="Delivery date"
          placeholder="mm/dd/yyyy"
          v-model="formData.scheduled_for"
          required
        />
      </div>
      <bdg-dropdown
        ref="user"
        name="Assigned to"
        :options="users"
        v-model="formData.user_id"
        searchable
        required
      />
      <bdg-text-field name="Ordered by" v-model="formData.ordered_by" />
      <bdg-text-field
        name="Price quote"
        v-model="formData.price_quote[i]"
        :key="`pq_field_${i}`"
        v-for="(quote, i) in formData.price_quote"
      />
      <bdg-add-field
        class="mt-6"
        add-text="Add price quote (optional)"
        v-model="formData.price_quote"
      />
      <bdg-text-area name="Note" v-model="formData.note.text" />
    </template>
  </form-layout>
</template>

<script>
import FormLayout from "../../layouts/FormLayout";
import BdgTextField from "../../../forms/fields/BdgTextField";
import BdgMultiSelect from "../../../forms/fields/BdgMultiSelect";
import BdgDropdown from "../../../forms/fields/BdgDropdown";
import {
  ProcurementStatuses,
  ProcurementStatusLabels
} from "../../../constants/contract-enums";
import BdgDateField from "../../../forms/fields/BdgDateField";
import BdgAddField from "../../../forms/fields/BdgAddField";
import BdgTextArea from "../../../forms/fields/BdgTextArea";
import { validateRequiredRefs } from "../../../helpers";

export default {
  name: "ProcurementForm",
  extends: FormLayout,
  components: {
    BdgTextArea,
    BdgAddField,
    BdgDateField,
    BdgDropdown,
    BdgMultiSelect,
    BdgTextField,
    FormLayout
  },
  props: {
    id: Number,
    name: String,
    type: String,
    items: { type: Array, default: () => [] },
    contact_id: Number,
    ordered_at: String,
    scheduled_for: String,
    user_id: Number,
    ordered_by: String,
    price_quote: {
      type: Array,
      default: () => []
    },
    note: {
      type: Object,
      default: () => {
        return { text: null };
      }
    }
  },
  data() {
    return {
      formData: {
        id: this.id,
        contract_id: this.$route.params.id,
        type: this.type,
        items: this.items.slice().map(i => {
          return {
            ...i,
            label: i.name,
            subLabel: i.vendorName,
            qty: i.qty,
            procurement_id: i.procurement_id
          };
        }),
        contact_id: this.contact_id,
        ordered_at: this.ordered_at,
        scheduled_for: this.scheduled_for,
        user_id: this.user_id,
        ordered_by: this.ordered_by,
        price_quote: this.price_quote,
        note: this.note
      },
      statuses: Object.values(ProcurementStatuses).map(key => {
        return {
          label: ProcurementStatusLabels[key],
          value: key
        };
      }),
      requiredRefs: [
        "type",
        "items",
        "vendor",
        "orderedAt",
        "scheduledFor",
        "user"
      ]
    };
  },
  computed: {
    allItems() {
      const allItems = this.$store.state.Items.items;
      if (allItems) {
        return allItems.map(i => {
          i.label = i.name;
          i.subLabel = i.vendorName;
          i.qty = 1;
          i.value = Object.assign({}, i);
          return i;
        });
      }
      return [];
    },
    availableItems() {
      return this.allItems.filter(i => {
        return this.formData.items.map(j => j.id).indexOf(i.id) === -1;
      });
    },
    vendors() {
      const vendors = this.$store.state.Contacts.vendors;
      if (vendors) {
        return vendors.map(v => {
          return {
            label: v.companyAndContactNamesString,
            value: v.id
          };
        });
      }
      return vendors;
    },
    titleText() {
      if (this.id) {
        return "Edit Procurement";
      } else {
        return "Add Procurement";
      }
    },
    users() {
      const users = this.$store.state.Users.users;
      if (users) {
        return users.map(u => {
          return {
            label: u.name,
            value: u.id
          };
        });
      }
      return users;
    },
    isStoringProcurement() {
      return this.$store.state.Contracts.Procurements.isStoringProcurement;
    },
    isUpdatingProcurement() {
      return this.$store.state.Contracts.Procurements.isUpdatingProcurement;
    },
    isLoading() {
      return this.id ? this.isUpdatingProcurement : this.isStoringProcurement;
    }
  },
  created() {
    this.$store.dispatch("getItems");
    this.$store.dispatch("getVendors");
    this.$store.dispatch("getUsers");
  },
  methods: {
    itemsSearch(text) {
      return this.availableItems.filter(
        item => item.label.toLowerCase().indexOf(text.toLowerCase()) > -1
      );
    },
    validate() {
      return validateRequiredRefs(this, true);
    },
    saveProcurement() {
      if (!this.validate()) {
        return;
      }
      const formattedFormData = Object.assign({}, this.formData);
      const priceQuotes = formattedFormData.price_quote.filter(pq => !!pq);
      formattedFormData.price_quote = priceQuotes.length
        ? JSON.stringify(priceQuotes)
        : null;

      if (this.id) {
        this.$store.dispatch("updateProcurement", formattedFormData);
      } else {
        this.$store.dispatch("storeProcurement", formattedFormData);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../scss/assets";

.procurement-form {
  .order-delivery {
    .bdg-date-field {
      @include evenlySpacedFlexChild(2, 40px);
    }
  }
}
</style>
